import React, { useState, useEffect } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import stripePromise from '../../config/stripe';
import StripePaymentForm from './StripePaymentForm';
import './UnifiedForm.css';

import { 
  FaUser, 
  FaPhone, 
  FaEnvelope, 
  FaHome,
  FaUsers,
  FaUtensils,
  FaBaby,
  FaCalculator,
  FaCreditCard,
  FaCheckCircle
} from 'react-icons/fa';

import { GiBarbecue } from 'react-icons/gi';

const UnifiedRegistrationForm = () => {
  const [formData, setFormData] = useState({
    teamName: '',
    headCookName: '',
    phone: '',
    email: '',
    address: '',
    
    bbqTeam: false,
    pitType: 'Offset',
    teamMembers: 1,
    
    jackpotCategories: {
      beans: false,
      fajita: false,
      panDeCampo: false,
      dessert: false,
    },
    
    kidsEntries: [],
    
    waiver: false,
    signature: '',
    comments: '',
  });

  const [totalPayment, setTotalPayment] = useState(0);
  const [paymentStep, setPaymentStep] = useState(false); 
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);

  const BBQ_TEAM_FEE = 200;
  const JACKPOT_FEE = 25;
  const KIDS_FEE = 25;

  useEffect(() => {
    let total = 0;
    
    if (formData.bbqTeam) {
      total += BBQ_TEAM_FEE;
    }
    
    const selectedJackpot = Object.values(formData.jackpotCategories).filter(Boolean).length;
    total += selectedJackpot * JACKPOT_FEE;

    total += formData.kidsEntries.length * KIDS_FEE;
    
    setTotalPayment(total);
  }, [formData.bbqTeam, formData.jackpotCategories, formData.kidsEntries]);

  
  const hasSelections = formData.bbqTeam || 
    Object.values(formData.jackpotCategories).some(Boolean) || 
    formData.kidsEntries.length > 0;

  
  const hasKidsEntries = formData.kidsEntries.length > 0;

  
  const getContactSectionInfo = () => {
    if (formData.bbqTeam && hasKidsEntries) {
      return {
        title: 'Team & Parent/Guardian Information',
        description: 'Primary contact for the BBQ team and parent/guardian information for kids participants'
      };
    } else if (formData.bbqTeam) {
      return {
        title: 'Team Contact Information',
        description: 'Primary contact information for your BBQ team'
      };
    } else if (hasKidsEntries) {
      return {
        title: 'Parent/Guardian Information',
        description: 'Contact information for parent or guardian of participating children'
      };
    } else {
      return {
        title: 'Contact Information',
        description: 'Your contact information for registration'
      };
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === 'checkbox' ? checked : value 
    });
  };

  const handleJackpotChange = (e) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      jackpotCategories: { 
        ...formData.jackpotCategories, 
        [name]: checked 
      }
    });
  };

  const addKidsEntry = () => {
    setFormData({
      ...formData,
      kidsEntries: [...formData.kidsEntries, {
        id: Date.now(),
        childName: '',
        childAge: '',
        ageDivision: 'Junior',
        allergies: ''
      }]
    });
  };

  const updateKidsEntry = (id, field, value) => {
    setFormData({
      ...formData,
      kidsEntries: formData.kidsEntries.map(entry =>
        entry.id === id ? { ...entry, [field]: value } : entry
      )
    });
  };

  const removeKidsEntry = (id) => {
    setFormData({
      ...formData,
      kidsEntries: formData.kidsEntries.filter(entry => entry.id !== id)
    });
  };

  const handleFormValidation = (e) => {
    e.preventDefault();
    
    if (totalPayment === 0) {
      alert('Please select at least one competition category to register for.');
      return;
    }

    
    const isKidsOnlyRegistration = !formData.bbqTeam && hasKidsEntries && !Object.values(formData.jackpotCategories).some(Boolean);
    const teamNameRequired = !isKidsOnlyRegistration;
    
    if ((teamNameRequired && !formData.teamName) || !formData.headCookName || !formData.phone || !formData.email || !formData.address) {
      alert('Please fill in all required contact information.');
      return;
    }

    if (!formData.waiver || !formData.signature) {
      alert('Please accept the waiver and provide your digital signature.');
      return;
    }

    
    for (const kid of formData.kidsEntries) {
      if (!kid.childName || !kid.childAge) {
        alert('Please complete all kids entry information.');
        return;
      }
    }

    
    handleRegistrationComplete();
  };

  const handleRegistrationComplete = async () => {
    try {
      const response = await fetch('https://sogovia-cookoff-api-3bba922d210c.herokuapp.com/api/registrations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(getRegistrationData()),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Registration failed: ${errorText}`);
      }

      const result = await response.json();
      console.log('Registration successful:', result);
      
      setPaymentSuccess(true);
      setRegistrationId(`REG0${result.id}`);
      
    } catch (error) {
      console.error('Registration failed:', error);
      alert(`Registration failed: ${error.message}`);
    }
  };

  const handlePaymentSuccess = (paymentResult) => {
    setPaymentSuccess(true);
    setRegistrationId(paymentResult.registrationId);
    console.log('Payment successful:', paymentResult);
  };

  const handlePaymentError = (error) => {
    console.error('Payment failed:', error);
    
  };

  const resetForm = () => {
    setPaymentStep(false);
    setPaymentSuccess(false);
    setRegistrationId(null);
    setFormData({
      teamName: '',
      headCookName: '',
      phone: '',
      email: '',
      address: '',
      bbqTeam: false,
      pitType: 'Offset',
      teamMembers: 1,
      jackpotCategories: {
        beans: false,
        fajita: false,
        panDeCampo: false,
        dessert: false,
      },
      kidsEntries: [],
      waiver: false,
      signature: '',
      comments: '',
    });
  };

  
  const getRegistrationData = () => {
    const isKidsOnlyRegistration = !formData.bbqTeam && hasKidsEntries && !Object.values(formData.jackpotCategories).some(Boolean);
    
    return {
      teamName: isKidsOnlyRegistration && formData.kidsEntries.length > 0 
        ? `${formData.kidsEntries[0].childName} (Junior Pitmaster)` 
        : formData.teamName,
      headCookName: formData.headCookName,
      phone: formData.phone,
      email: formData.email,
      address: formData.address,
      
      bbqTeam: formData.bbqTeam,
      pitType: formData.bbqTeam ? formData.pitType : null,
      teamMembers: formData.bbqTeam ? formData.teamMembers : null,
      
      jackpotCategories: {
        beans: formData.jackpotCategories.beans,
        fajita: formData.jackpotCategories.fajita,
        panDeCampo: formData.jackpotCategories.panDeCampo,
        dessert: formData.jackpotCategories.dessert
      },
      
      kidsEntries: formData.kidsEntries.map(entry => ({
        childName: entry.childName,
        childAge: parseInt(entry.childAge),
        ageDivision: entry.ageDivision,
        allergies: entry.allergies || null
      })),
      
      waiver: formData.waiver,
      signature: formData.signature,
      comments: formData.comments || null,
      totalPayment: totalPayment
    };
  };

  const contactInfo = getContactSectionInfo();

  
  if (paymentSuccess) {
    return (
      <div className="unified-registration-container">
        <div className="success-message">
          <div className="success-content">
            <FaCheckCircle className="success-icon" />
            <h1>Registration Successful!</h1>
            <p>Thank you for registering for the BBQ Competition!</p>
            <div className="success-details">
              <p><strong>Registration ID:</strong> {registrationId}</p>
              <p><strong>Total Amount Due:</strong> ${totalPayment.toFixed(2)}</p>
              <p><strong>Email:</strong> {formData.email}</p>
              <div className="payment-instructions">
                <p><strong>Next Steps:</strong> You will receive a payment link via email closer to the event date. Please keep your registration ID for your records.</p>
              </div>
            </div>
            <div className="success-actions">
              <button onClick={resetForm} className="btn btn-secondary">
                Register Another Team
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="unified-registration-container">
      <div className="registration-header">
        <h1><GiBarbecue className="header-icon" />Complete Registration</h1>
        <p>First, select what you'd like to register for, then complete your information</p>
      </div>

      <form onSubmit={handleFormValidation} className="unified-form">
        
        
        <div className="form-section">
          <div className="section-header">
            <FaUtensils className="section-icon" />
            <h2>Select Competitions</h2>
          </div>
          
          
          <div className="competition-option">
            <div className="option-header">
              <label className="competition-checkbox">
                <input 
                  type="checkbox" 
                  name="bbqTeam" 
                  checked={formData.bbqTeam} 
                  onChange={handleChange} 
                />
                <span className="checkbox-custom"></span>
                <div className="competition-info">
                  <h3><GiBarbecue className="comp-icon" />CBA BBQ Team Competition</h3>
                  <p>Includes Chicken, Ribs, and Brisket categories</p>
                  <div className="price-tag">${BBQ_TEAM_FEE}</div>
                </div>
              </label>
            </div>
            
            {formData.bbqTeam && (
              <div className="competition-details">
                <div className="form-row">
                  <div className="form-group">
                    <label>Pit Type</label>
                    <select name="pitType" value={formData.pitType} onChange={handleChange}>
                      <option>Offset</option>
                      <option>Pellet</option>
                      <option>Drum</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Number of Team Members</label>
                    <input 
                      type="number" 
                      name="teamMembers" 
                      min="1" 
                      value={formData.teamMembers} 
                      onChange={handleChange} 
                    />
                  </div>
                </div>
                <div className="included-categories">
                  <h4>Included Categories:</h4>
                  <div className="category-badges">
                    <span className="category-badge">Chicken</span>
                    <span className="category-badge">Ribs</span>
                    <span className="category-badge">Brisket</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          
          <div className="competition-option">
            <div className="option-header">
              <h3><FaUtensils className="comp-icon" />Jackpot Categories <span className="optional">(Optional)</span></h3>
              <p>Additional competition categories - ${JACKPOT_FEE} each</p>
            </div>
            
            <div className="competition-details">
              <div className="jackpot-categories">
                {Object.entries(formData.jackpotCategories).map(([category, checked]) => (
                  <label key={category} className="jackpot-option">
                    <input 
                      type="checkbox" 
                      name={category} 
                      checked={checked} 
                      onChange={handleJackpotChange} 
                    />
                    <span className="checkbox-custom"></span>
                    <span className="category-name">
                      {category === 'panDeCampo' ? 'Pan de Campo' : 
                       category === 'dessert' ? 'Dessert' :
                       category.charAt(0).toUpperCase() + category.slice(1)}
                    </span>
                    <span className="category-price">${JACKPOT_FEE}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Kids Cookoff */}
          <div className="competition-option">
            <div className="option-header">
              <h3><FaBaby className="comp-icon" />Junior Pitmasters Challenge <span className="optional">(Optional)</span></h3>
              <p>Kids competition for ages 5-17 - ${KIDS_FEE} per child</p>
              <button 
                type="button" 
                className="btn btn-secondary btn-small" 
                onClick={addKidsEntry}
              >
                + Add Child
              </button>
            </div>
            
            {formData.kidsEntries.length > 0 && (
              <div className="competition-details">
                <div className="kids-entries">
                  {formData.kidsEntries.map((entry, index) => (
                    <div key={entry.id} className="kids-entry">
                      <div className="entry-header">
                        <h4>Child {index + 1}</h4>
                        <button 
                          type="button" 
                          className="btn-remove" 
                          onClick={() => removeKidsEntry(entry.id)}
                        >
                          ×
                        </button>
                      </div>
                      
                      <div className="form-row">
                        <div className="form-group">
                          <label>Child's Full Name</label>
                          <input 
                            type="text" 
                            value={entry.childName} 
                            onChange={(e) => updateKidsEntry(entry.id, 'childName', e.target.value)}
                            required 
                            placeholder="Child's full name"
                          />
                        </div>
                        <div className="form-group">
                          <label>Age</label>
                          <input 
                            type="number" 
                            min="5" 
                            max="17" 
                            value={entry.childAge} 
                            onChange={(e) => updateKidsEntry(entry.id, 'childAge', e.target.value)}
                            required 
                          />
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label>Age Division</label>
                        <div className="radio-group">
                          <label>
                            <input 
                              type="radio" 
                              name={`ageDivision-${entry.id}`}
                              value="Junior" 
                              checked={entry.ageDivision === 'Junior'} 
                              onChange={(e) => updateKidsEntry(entry.id, 'ageDivision', e.target.value)}
                            />
                            Junior (5-10)
                          </label>
                          <label>
                            <input 
                              type="radio" 
                              name={`ageDivision-${entry.id}`}
                              value="Senior" 
                              checked={entry.ageDivision === 'Senior'} 
                              onChange={(e) => updateKidsEntry(entry.id, 'ageDivision', e.target.value)}
                            />
                            Senior (11-17)
                          </label>
                        </div>
                      </div>
                      
                      <div className="form-group">
                        <label>Allergies/Special Needs (Optional)</label>
                        <textarea 
                          value={entry.allergies} 
                          onChange={(e) => updateKidsEntry(entry.id, 'allergies', e.target.value)}
                          placeholder="Any allergies or special accommodations needed..."
                        ></textarea>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {!hasSelections && (
            <div className="no-selections-note">
              <p>Please select at least one competition above to continue with registration</p>
            </div>
          )}
        </div>

        
        {hasSelections && (
          <div className="form-section contact-section">
            <div className="section-header">
              <FaUser className="section-icon" />
              <div className="section-info">
                <h2>{contactInfo.title}</h2>
                <p className="section-description">{contactInfo.description}</p>
              </div>
            </div>
            
            <div className="form-row">
              
              {(formData.bbqTeam || !hasKidsEntries || Object.values(formData.jackpotCategories).some(Boolean)) && (
                <div className="form-group">
                  <label>
                    <FaUsers className="label-icon" />
                    {formData.bbqTeam ? 'Team Name' : 'Participant Name'}
                  </label>
                  <input 
                    type="text" 
                    name="teamName" 
                    value={formData.teamName} 
                    onChange={handleChange} 
                    required 
                    placeholder={formData.bbqTeam ? 'Enter your team name' : 'Enter participant name'}
                  />
                </div>
              )}
              <div className="form-group">
                <label>
                  <FaUser className="label-icon" />
                  {formData.bbqTeam ? 'Head Cook Name' : hasKidsEntries ? 'Parent/Guardian Name' : 'Contact Name'}
                </label>
                <input 
                  type="text" 
                  name="headCookName" 
                  value={formData.headCookName} 
                  onChange={handleChange} 
                  required 
                  placeholder="Full name of primary contact"
                />
              </div>
            </div>
            
            <div className="form-row">
              <div className="form-group">
                <label><FaPhone className="label-icon" />Phone Number</label>
                <input 
                  type="tel" 
                  name="phone" 
                  value={formData.phone} 
                  onChange={handleChange} 
                  required 
                  placeholder="(956) 123-4567"
                />
              </div>
              <div className="form-group">
                <label><FaEnvelope className="label-icon" />Email Address</label>
                <input 
                  type="email" 
                  name="email" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                  placeholder="your.email@example.com"
                />
              </div>
            </div>
            
            <div className="form-group">
              <label><FaHome className="label-icon" />Mailing Address</label>
              <input 
                type="text" 
                name="address" 
                value={formData.address} 
                onChange={handleChange} 
                required 
                placeholder="Street address, city, state, zip"
              />
            </div>
          </div>
        )}

        
        {hasSelections && (
          <div className="form-section payment-section">
            <div className="section-header">
              <FaCalculator className="section-icon" />
              <h2>Payment Summary</h2>
            </div>
            
            <div className="payment-breakdown">
              {formData.bbqTeam && (
                <div className="payment-item">
                  <span>CBA BBQ Team Competition</span>
                  <span>${BBQ_TEAM_FEE}</span>
                </div>
              )}
              
              {Object.entries(formData.jackpotCategories).map(([category, checked]) => 
                checked && (
                  <div key={category} className="payment-item">
                    <span>Jackpot - {category === 'panDeCampo' ? 'Pan de Campo' : 
                      category === 'dessert' ? 'Dessert' :
                      category.charAt(0).toUpperCase() + category.slice(1)}</span>
                    <span>${JACKPOT_FEE}</span>
                  </div>
                )
              )}
              
              {formData.kidsEntries.map((entry, index) => (
                <div key={entry.id} className="payment-item">
                  <span>Kids Cookoff - {entry.childName || `Child ${index + 1}`}</span>
                  <span>${KIDS_FEE}</span>
                </div>
              ))}
              
              <div className="payment-total">
                <span>Total Amount</span>
                <span>${totalPayment}</span>
              </div>
            </div>
          </div>
        )}

        
        {hasSelections && (
          <div className="form-section">
            <div className="form-group">
              <label>Additional Comments (Optional)</label>
              <textarea 
                name="comments" 
                value={formData.comments} 
                onChange={handleChange}
                placeholder="Any special requests, questions, or additional information..."
              ></textarea>
            </div>
          </div>
        )}

        
        {hasSelections && (
          <div className="form-section legal-section">
            <div className="form-group">
              <label className="waiver-label">
                <input 
                  type="checkbox" 
                  name="waiver" 
                  checked={formData.waiver} 
                  onChange={handleChange} 
                  required 
                />
                <FaCheckCircle className="check-icon" />
                I agree to the terms and conditions, liability waiver, and event rules.
                {hasKidsEntries && ' As a parent/guardian, I consent for any children registered.'}
              </label>
            </div>
            
            <div className="form-group">
              <label>Digital Signature (Type your full name)</label>
              <input 
                type="text" 
                name="signature" 
                value={formData.signature} 
                onChange={handleChange} 
                required 
                placeholder="Type your full legal name as your digital signature"
              />
            </div>
          </div>
        )}

        
        {hasSelections && !paymentStep && (
          <div className="submit-section">
            <div className="payment-notice">
              <p><strong>Payment Information:</strong> You will receive a payment link via email closer to the event date where payment can be completed.</p>
            </div>
            <button type="submit" className="btn btn-primary btn-large">
              <FaCheckCircle className="btn-icon" />
              Complete Registration
              <span className="btn-arrow">→</span>
            </button>
          </div>
        )}

        
        {/* 
        {hasSelections && paymentStep && (
          <div>
            <Elements stripe={stripePromise}>
              <StripePaymentForm 
                registrationData={getRegistrationData()}
                onPaymentSuccess={handlePaymentSuccess}
                onPaymentError={handlePaymentError}
              />
            </Elements>
          </div>
        )}
        */}
      </form>
    </div>
  );
};

export default UnifiedRegistrationForm; 